import { SimpleEventDispatcher } from "ste-simple-events";
import { getAndStoreTestGroupInformation } from "../../services/variant-test";
import { ILog } from "@ihr-radioedit/inferno-core";
import { localStorage } from "../../services/local-storage";
import type { Store } from "../../stores";
import { SessionBackend, IHRSessionState, isIHeartDomain } from "../../decoders/ihr-user";
import { getProfile, logout } from "../../services/ihr-client";

const log = ILog.logger("IHM Session/ihr-client.ts");

function shouldUseOAuthFlow(): boolean {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("legacyAuthFlow") === "true") {
      return false;
    }
    if (params.get("oauthFlow") === "true") {
      return true;
    }
    return !isIHeartDomain(window.location.hostname);
  }
  return false;
}

export class IHRSessionBackend implements SessionBackend {
  readonly name = "ihr-auth";
  state: IHRSessionState = { authenticated: false, pid: "", aid: "" };
  private baseUri: string;

  private oauthHost: string;
  private oauthInfernoLogoutPath: string;
  private oauthInfernoProfilePath: string;
  private oauthSignupPath: string;
  private oauthLoginPath: string;
  private _onStatusChanged = new SimpleEventDispatcher<IHRSessionState>();

  constructor(private store: Store, private depAbTestHost: string, private depRequestTimeout: number) {
    if (
      !this.store.env.WEB_ACCOUNT_HOST ||
      !this.store.env.OAUTH_SIGNUP_PATH ||
      !this.store.env.OAUTH_LOGIN_PATH ||
      !this.store.env.OAUTH_INFERNO_LOGOUT_PATH ||
      !this.store.env.OAUTH_INFERNO_PROFILE_PATH ||
      !this.store.env.NODE_ENV
    ) {
      throw new Error("Required Environment vars not found for IHR Session");
    }

    this.oauthHost = this.store.env.WEB_ACCOUNT_HOST;
    this.oauthSignupPath = this.store.env.OAUTH_SIGNUP_PATH;
    this.oauthLoginPath = this.store.env.OAUTH_LOGIN_PATH;

    this.baseUri = this.store.api_base_uri;
    this.oauthInfernoLogoutPath = this.store.env.OAUTH_INFERNO_LOGOUT_PATH;
    this.oauthInfernoProfilePath = this.store.env.OAUTH_INFERNO_PROFILE_PATH;

    const stored = this.local();
    if (stored) {
      this.state = stored;
      this._onStatusChanged.dispatch(this.state);
    }
  }

  get onStatusChanged() {
    return this._onStatusChanged.asEvent();
  }

  get authenticated() {
    return this.state.authenticated;
  }

  get hasPrivacy() {
    return !!this.state?.profile?.privacy;
  }

  async login() {
    try {
      let targetUrl: URL;

      if (shouldUseOAuthFlow()) {
        targetUrl = this.buildOAuthAuthorizeUrl(this.store.env.OAUTH_AUTHORIZE_PATH);
      } else {
        targetUrl = this.getTargetUrlWithRedirects(new URL(this.oauthLoginPath, this.oauthHost));
      }

      const loginToken = this.store.request.query?.loginToken ?? undefined;
      if (loginToken) {
        targetUrl.searchParams.set("loginToken", loginToken as string);
      }

      window.location.href = targetUrl.toString();
    } catch (_e) {
      log.error("Failed to login", _e);
      throw this.state;
    }
  }

  async signup() {
    try {
      let signUpUrl: URL;

      if (shouldUseOAuthFlow()) {
        signUpUrl = this.buildOAuthAuthorizeUrl(this.oauthSignupPath);
      } else {
        signUpUrl = this.getTargetUrlWithRedirects(new URL(this.oauthSignupPath, this.oauthHost));
      }

      window.location.href = signUpUrl.toString();
    } catch (_e) {
      log.error("Failed to signup", _e);
      throw this.state;
    }
  }

  async logout() {
    try {
      const oauthLogoutUrl = new URL(this.baseUri + this.oauthInfernoLogoutPath, this.getBase(this.getRedirectHost()));
      const updatedOauthLogoutUrl = this.restoreHostnameSuffix(oauthLogoutUrl);

      const result = await logout(updatedOauthLogoutUrl.toString());
      if (result) {
        await this.sessionUpdated({ authenticated: false, aid: "", pid: "" });
        window.location.reload();
        return this.state;
      }

      throw this.state;
    } catch (_e) {
      log.error("Failed to logout", _e);
      throw this.state;
    }
  }

  async refresh() {
    try {
      const profileUrl = new URL(this.baseUri + this.oauthInfernoProfilePath, this.getBase(this.getRedirectHost()));
      const updatedProfileUrl = this.restoreHostnameSuffix(profileUrl);

      const result = await getProfile(updatedProfileUrl.toString());
      if (result && result?.profile) {
        const { sessionId, profileId } = result.profile;
        return this.sessionUpdated({
          ...this.state,
          profile: result.profile,
          aid: sessionId,
          pid: String(profileId),
          authenticated: true,
        });
      }

      return this.sessionUpdated({ authenticated: false, aid: "", pid: "" });
    } catch (_e) {
      log.error("Failed to refresh", _e);
      throw this.state;
    }
  }

  private getRedirectHost() {
    const prHost = this.store.request.prHostname;
    const localStagingProdHost = this.store.request.hostname;

    return prHost || localStagingProdHost!;
  }

  private getBase(hostName: string) {
    let protocol = this.store.request.protocol;
    if (!protocol.endsWith(":")) {
      protocol += ":";
    }

    return `${protocol}//${hostName}`;
  }

  // HACK: VCL/CDN strips --{suffix} (e.g. --preprod, --{buildId}) from the hostname before it reaches RE.
  // This re-inserts the suffix from the browser's actual hostname so API calls and redirects go to the correct build.
  private restoreHostnameSuffix(url: URL) {
    const suffix = this.getHostnameSuffix();
    if (suffix) {
      url.hostname = this.insertHostnameSuffix(url.hostname, suffix);
    }
    return url;
  }

  private getHostnameSuffix(): string | null {
    const match = location.hostname.match(/--([^.]+)\.inferno\.iheart\.com/);
    return match ? match[1] : null;
  }

  private insertHostnameSuffix(hostName: string, suffix: string) {
    return hostName.replace(/(\w+-\w{2})(\.inferno\.iheart\.com)/, `$1--${suffix}$2`);
  }

  /**
   * Builds an OAuth code-exchange URL for non-iHeart domains.
   */
  private buildOAuthAuthorizeUrl(path: string): URL {
    const authorizeUrl = new URL(path, this.oauthHost);
    const base = this.getBase(this.getRedirectHost());

    authorizeUrl.searchParams.set("client_id", this.store.env.OAUTH_NON_IHR_CLIENT_ID);
    authorizeUrl.searchParams.set("redirect_uri", `${base}/api/v4/auth/verify/`);
    authorizeUrl.searchParams.set("response_type", "code");

    const redirectUrl = new URL(this.store.request.path, base);
    const updatedRedirectUrl = this.restoreHostnameSuffix(redirectUrl);

    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.forEach((value, key) => {
      if (key.toLowerCase() === "logintoken") {
        return;
      }
      updatedRedirectUrl.searchParams.append(key, value);
    });

    authorizeUrl.searchParams.set("state", updatedRedirectUrl.toString());
    return authorizeUrl;
  }

  // NOTE: oauth flows in regards to PR/Preprod builds will only work when --pr or --preprod flag is in the url. Direct access to RE builds will not work.
  private getTargetUrlWithRedirects(url: URL) {
    const redirectUrl = new URL(this.store.request.path, this.getBase(this.getRedirectHost()));

    const updatedRedirectUrl = this.restoreHostnameSuffix(redirectUrl);

    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.forEach((value, key) => {
      if (key.toLowerCase() === "logintoken") {
        return;
      }
      updatedRedirectUrl.searchParams.append(key, value);
    });

    url.searchParams.set("redirectUrl", updatedRedirectUrl.toString());

    return url;
  }

  private static stateDataValid(state: IHRSessionState) {
    if (state.authenticated && state.pid && state.aid) {
      return true;
    }
    log.warn("State data is invalid: ", state);
    return false;
  }

  private local() {
    const localData = localStorage.getItem(this.name);
    if (localData) {
      try {
        const state = JSON.parse(localData) as IHRSessionState;
        if (IHRSessionBackend.stateDataValid(state)) {
          log.debug("Found local session data");
          return state;
        } else {
          log.warn("Invalid session data stored, removing it from localStorage");
          localStorage.removeItem(this.name);
        }
      } catch (e) {
        log.debug("Invalid payload in local storage");
      }
    }
    return null;
  }

  private persist(update: IHRSessionState) {
    log.debug("Persisting session data", update);

    this.state = { ...update };

    try {
      localStorage.setItem(this.name, JSON.stringify(this.state));
    } catch (err) {
      log.error(err.message);
      this.persist({ authenticated: update.authenticated, pid: "", aid: "" });
    }
  }

  private async sessionUpdated(state: IHRSessionState) {
    const { authenticated, aid, pid, profile } = state;

    await getAndStoreTestGroupInformation(this.store, this.depAbTestHost, this.depRequestTimeout);

    let persistData: IHRSessionState = {
      authenticated,
      pid: "",
      aid: "",
      profile: undefined,
    };

    if (authenticated) {
      if (profile?.isAnonymous) {
        log.warn("Invalid Account Type: Anonymous");
      } else {
        persistData = { authenticated, pid, aid, profile };
      }
    }

    this.persist(persistData);

    this._onStatusChanged.dispatch(this.state);
    return this.state;
  }
}
